<template>
  <div class="page-view">
    <div class="header-row">
      <div>
        <h2>产品列表</h2>
        <p>维护各业务类型下可选产品，用于手工新建和编辑订单。</p>
      </div>
      <el-button type="primary" @click="openCreate">新增产品</el-button>
    </div>

    <div class="brand-card filter-card">
      <div class="toolbar-row">
        <el-input v-model="filters.keyword" placeholder="产品名称" clearable />
        <el-select v-model="filters.business_type_id" placeholder="业务类型" clearable filterable>
          <el-option v-for="item in businessTypeOptions" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
        <el-select v-model="filters.is_active" placeholder="状态" clearable>
          <el-option label="启用" :value="true" />
          <el-option label="停用" :value="false" />
        </el-select>
        <el-button type="primary" plain @click="handleQuery">查询</el-button>
      </div>
    </div>

    <div class="brand-card table-card">
      <el-table :data="items" stripe>
        <el-table-column prop="id" label="产品ID" width="100" />
        <el-table-column prop="name" label="产品名称" min-width="180" />
        <el-table-column prop="business_type_name" label="业务类型" min-width="160" />
        <el-table-column prop="created_by_name" label="创建人" min-width="120" />
        <el-table-column v-if="currentUser.role === 'super_admin'" label="可用管理员" min-width="220">
          <template slot-scope="{ row }">{{ (row.admin_names || []).join("、") || "-" }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">{{ row.is_active ? "启用" : "停用" }}</template>
        </el-table-column>
        <el-table-column label="操作" width="260" fixed="right">
          <template slot-scope="{ row }">
            <template v-if="canManageItem(row)">
              <el-button size="mini" @click="openEdit(row)">编辑</el-button>
              <el-button size="mini" plain @click="toggleStatus(row)">{{ row.is_active ? "停用" : "启用" }}</el-button>
              <el-button size="mini" type="danger" plain @click="removeItem(row)">删除</el-button>
            </template>
            <span v-else class="readonly-text">仅可使用</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="pager-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :page-size="pager.page_size"
          :current-page="pager.page"
          :page-sizes="[10, 20, 50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog
      :title="editingId ? '编辑产品' : '新增产品'"
      :visible.sync="showDialog"
      width="520px"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="100px">
        <el-form-item label="业务类型">
          <el-select v-model="form.business_type_id" style="width: 100%;" filterable clearable>
            <el-option v-for="item in businessTypeOptions" :key="item.id" :label="item.name" :value="item.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="产品名称">
          <el-input v-model.trim="form.name" />
        </el-form-item>
        <el-form-item v-if="canEditAdminsInForm" label="可用管理员">
          <el-checkbox-group v-model="form.admin_ids">
            <el-checkbox v-for="leader in leaders" :key="leader.id" :label="leader.id">
              {{ leader.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showDialog = false">取消</el-button>
        <el-button type="primary" @click="submitForm">提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { createProduct, deleteProduct, fetchBusinessTypes, fetchLeaders, fetchProducts, updateProduct } from "../api/orders";
import { getStoredUser } from "../utils/auth";

const emptyForm = () => ({ name: "", business_type_id: null, admin_ids: [], created_by_role: "" });

export default {
  data() {
    return {
      filters: { keyword: "", business_type_id: "", is_active: "" },
      currentUser: getStoredUser(),
      businessTypeOptions: [],
      leaders: [],
      items: [],
      total: 0,
      pager: { page: 1, page_size: 10 },
      showDialog: false,
      editingId: null,
      form: emptyForm(),
    };
  },
  computed: {
    canEditAdminsInForm() {
      return this.currentUser.role === "super_admin" && (!this.editingId || this.form.created_by_role === "super_admin");
    },
  },
  mounted() {
    this.loadBusinessTypes();
    this.loadLeaders();
    this.loadItems();
  },
  methods: {
    getErrorMessage(error, fallback) {
      const detail = error.response?.data?.detail;
      if (typeof detail === "string" && detail) {
        return detail;
      }
      if (Array.isArray(detail)) {
        return detail
          .map((item) => item.msg || item.message || item.detail)
          .filter(Boolean)
          .join("; ") || fallback;
      }
      return fallback;
    },
    async loadLeaders() {
      if (this.currentUser.role !== "super_admin") return;
      const { data } = await fetchLeaders();
      this.leaders = data || [];
    },
    async loadBusinessTypes() {
      const { data } = await fetchBusinessTypes({ page: 1, page_size: 1000, is_active: true });
      this.businessTypeOptions = data.items || [];
    },
    async loadItems() {
      const { data } = await fetchProducts({
        keyword: this.filters.keyword || undefined,
        business_type_id: this.filters.business_type_id || undefined,
        is_active: this.filters.is_active === "" ? undefined : this.filters.is_active,
        page: this.pager.page,
        page_size: this.pager.page_size,
      });
      this.items = (data.items || []).map((item) => ({ ...item, admin_ids: [...(item.admin_ids || [])] }));
      this.total = data.total || 0;
    },
    handleQuery() {
      this.pager.page = 1;
      this.loadItems();
    },
    onPageChange(page) {
      this.pager.page = page;
      this.loadItems();
    },
    onSizeChange(size) {
      this.pager.page = 1;
      this.pager.page_size = size;
      this.loadItems();
    },
    openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      this.showDialog = true;
    },
    openEdit(row) {
      this.editingId = row.id;
      this.form = {
        name: row.name,
        business_type_id: row.business_type_id,
        admin_ids: [...(row.admin_ids || [])],
        created_by_role: row.created_by_role || "",
      };
      this.showDialog = true;
    },
    canManageItem(row) {
      return this.currentUser.role === "super_admin" || row.created_by_id === this.currentUser.id;
    },
    validateForm() {
      if (!this.form.business_type_id) {
        this.$message.warning("请选择业务类型");
        return false;
      }
      if (!this.form.name) {
        this.$message.warning("请输入产品名称");
        return false;
      }
      return true;
    },
    async submitForm() {
      if (!this.validateForm()) return;
      try {
        const payload = { ...this.form };
        delete payload.created_by_role;
        if (!this.canEditAdminsInForm) {
          delete payload.admin_ids;
        }
        if (this.editingId) {
          await updateProduct(this.editingId, payload);
          this.$message.success("更新成功");
        } else {
          await createProduct(payload);
          this.$message.success("创建成功");
        }
        this.showDialog = false;
        this.loadItems();
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, "提交失败"));
      }
    },
    async toggleStatus(row) {
      try {
        await updateProduct(row.id, { is_active: !row.is_active });
        this.$message.success("状态已更新");
        this.loadItems();
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, "更新失败"));
      }
    },
    async removeItem(row) {
      await this.$confirm(`确认删除产品 ${row.name} 吗？`, "删除产品", { type: "warning" });
      try {
        await deleteProduct(row.id);
        this.$message.success("删除成功");
        this.loadItems();
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, "删除失败"));
      }
    },
  },
};
</script>

<style scoped>
:deep(.el-checkbox-group) {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}

:deep(.el-checkbox) {
  margin-right: 0;
}

.readonly-text {
  color: #8b97aa;
  font-size: 13px;
}
</style>
